export interface InvestorHistoryEntry {
  id: string;
  timestamp: string;
  action: string;
  details: string;
  userId?: string;
}

export interface NetWorthRequirements {
  delegated?: number;
  nonDelegated?: number;
}

export type EpoRuleType = 'payments' | 'days_since_funding' | 'days_since_purchase' | 'calendar_month_end_after_purchase';

export interface EpoRule {
  type: EpoRuleType;
  value: number | undefined;
  description: string;
}

export interface EpdRule {
  type: 'payments' | 'days_since_funding' | 'days_since_purchase';
  value: number | undefined;
  description: string;
}

// Manage Investors v2: one row shared by the independent Processing and Closing contact lists.
export interface InvestorOperationalContact {
  label?: string;
  name?: string;
  email?: string;
  phone?: string;
}

// Manage Investors v2: repeating net-worth requirement row (distinct from the
// legacy delegated/non-delegated numerics on NetWorthRequirements).
export interface InvestorNetWorthRow {
  label?: string;
  amount?: number | null;
  notes?: string;
}

export type CredentialChannel = 'shared' | 'wholesale' | 'correspondent';

// Manage Investors v2: a per-user investor portal login. The password ciphertext
// never reaches the client via list reads — only `revealCredential` returns the
// decrypted value (audited for admins). `hasPassword` indicates a stored secret.
export interface InvestorUserCredential {
  id: string;
  investorId: string;
  userId: string;
  channel: CredentialChannel;
  username?: string | null;
  notes?: string | null;
  hasPassword: boolean;
  isOwn: boolean;
  ownerName?: string | null;
  updatedAt?: string;
}

export interface Investor {
  servicerSelectionMode?: 'product_scope' | 'per_loan';
  id: string;
  investor: string;
  draftStatus?: 'draft' | 'active'; // Draft support for enterprise draft management
  draftCreatedAt?: string; // When draft was initially created
  draftLastSavedAt?: string; // Last auto-save timestamp
  productType: string[]; // Legacy field - kept for backward compatibility
  docType?: string[]; // For Non-QM document type selection (supports multiple)
  canDoWholesale: boolean; // New field to control wholesale capability
  canGoCorrespondent: boolean;
  correspondentStatus: 'Approved' | 'Pending' | 'Inactive';
  delegatedType?: 'Delegated' | 'Non-Delegated' | 'Delegated/Non-Delegated'; // Legacy; read-only display fallback (item 6)
  /** Item 6: per-product delegation (authoritative). Product names are a subset of correspondentProducts. */
  delegation?: { delegated: string[]; nonDelegated: string[] };
  netWorthRequirements: NetWorthRequirements;

  // Per-channel product availability
  wholesaleProducts?: string[];
  wholesaleLenderPaidComp?: number | null; // Lender paid compensation percentage (1.00 - 2.50) or null for N/A
  wholesaleAdditionalLendwiseFee?: number | null; // Additional fee charged by Lendwise in dollars
  correspondentProducts?: string[];

  // Wholesale AE information
  wholesaleAeName?: string;
  wholesaleAePhone?: string;
  wholesaleAeEmail?: string;

  // Correspondent AE information
  correspondentAeName?: string;
  correspondentAePhone?: string;
  correspondentAeEmail?: string;

  // Legacy AE information (keep for backward compatibility)
  aeName: string;
  aePhone: string;
  aeEmail: string;

  status: 'Approved' | 'Pending' | 'Inactive'; // Now represents wholesale status
  approvedAsOfDate?: string;
  correspondentApprovedAsOfDate?: string;

  // Wholesale approved date tracking (for activity feed)
  wholesaleApprovedDate?: string;

  // Wholesale credentials and website
  wholesaleWebsite?: string;
  wholesaleUsername?: string;
  wholesalePassword?: string;

  // Correspondent credentials and website
  correspondentWebsite?: string;
  correspondentUsername?: string;
  correspondentPassword?: string;

  // Optimal Blue integration
  wholesaleCanIntegrateOptimalBlue?: boolean;
  wholesaleHasIntegratedOptimalBlue?: boolean;
  correspondentCanIntegrateOptimalBlue?: boolean;
  correspondentHasIntegratedOptimalBlue?: boolean;

  // Channel-specific business rules
  wholesaleEpoRule: EpoRule; // Wholesale Early Pay Off rule
  wholesaleEpdRule: EpdRule; // Wholesale Early Payment Default rule
  correspondentEpoRule: EpoRule; // Correspondent Early Pay Off rule
  correspondentEpdRule: EpdRule; // Correspondent Early Payment Default rule
  repurchaseRule: string; // Shared repurchase rule

  // Legacy business rules (keep for backward compatibility during migration)
  epoRule?: string; // Will be deprecated
  epdRule?: string; // Will be deprecated

  // Channel-specific notes
  wholesaleNotes?: string;
  correspondentNotes?: string;

  // Clarifii reliance letter config
  clarifiRelianceRequired?: boolean;
  clarifiRelianceInvestorRecipients?: string[];

  // Manage Investors v2: Processing operational fields.
  howToGetCredentials?: string;
  portalNotes?: string;
  // Closing portal access is intentionally independent from Processing.
  closingWholesaleWebsite?: string;
  closingCorrespondentWebsite?: string;
  closingHowToGetCredentials?: string;
  closingPortalNotes?: string;
  fhaLenderId?: string;
  vaLenderId?: string;
  mersOrgId?: string;
  taxId?: string;
  docPlanCodes?: string;
  submissionInstructions?: string;
  submissionNotes?: string;
  deliveryInstructions?: string;
  deliveryNotes?: string;
  deliveryMethod?: 'Investor Connect' | 'Other' | null;
  deliveryMethodOther?: string;
  closingRelianceLetterRequired?: boolean; // distinct from clarifiRelianceRequired
  operationalContacts?: InvestorOperationalContact[];
  closingOperationalContacts?: InvestorOperationalContact[];
  netWorthRequirementRows?: InvestorNetWorthRow[];

  // Legacy fields (keep for backward compatibility)
  website: string;
  adminUsername: string;
  password: string;
  notes: string;
  history: InvestorHistoryEntry[];
  documents: InvestorDocument[]; // Document management system
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  updatedBy?: string; // User ID who last modified this record
  updatedByName?: string; // Full name of user who last modified this record
  correspondentLockedAt?: string; // Timestamp when correspondent was first enabled via dashboard
  originalApprovedAsOfDate?: string; // Original wholesale approval date to restore when cycling back
  originalCorrespondentApprovedAsOfDate?: string; // Original correspondent approval date to restore when cycling back
}

export interface DateRangeFilter {
  startDate: string;
  endDate: string;
}

export interface InvestorFilters {
  search: string;
  status: 'All' | 'Approved' | 'Pending' | 'Inactive';
  productType: string;
  delegatedType: 'All' | 'Delegated' | 'Non-Delegated' | 'Delegated/Non-Delegated';
  correspondentStatus: string[];
  wholesaleStatus: string[];
  wholesaleObIntegrated: 'All' | 'Yes' | 'No';
  correspondentObIntegrated: 'All' | 'Yes' | 'No';
  approvedDateRange: DateRangeFilter;
  correspondentApprovedDateRange: DateRangeFilter;
  createdDateRange: DateRangeFilter;
  netWorthMin: string;
  netWorthMax: string;
}

// Document Management System Types
export interface InvestorDocument {
  id: string;
  investorId: string;
  documentType: 'wholesale' | 'correspondent'; // New field to separate wholesale and correspondent docs
  category: DocumentCategory;
  originalFileName: string;
  displayName: string | null; // Explicit custom title. NULL means use the original file name.
  fileUrl: string; // In real app, this would be a URL to the file storage
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  uploadedBy: string;
  isActive: boolean; // true for active, false for archived
  version: number; // Version number for tracking
  /** Item 10: lineage id — versions of one logical document share a document_group_id. */
  documentGroupId?: string;
  notes?: string;

  // Storage fields (for worker access)
  storagePath?: string;
  storageBucket?: string;

  // Extraction fields (Docling text extraction)
  content?: string;
  extractionStatus?: 'pending' | 'extracting' | 'completed' | 'failed' | 'skipped';
  extractionError?: string;
  extractionStartedAt?: string;
  extractedAt?: string;
  totalPages?: number;
  extractionTimeMs?: number;

  // Credit Guidelines sub-type
  subType?: string;
  // Credit Matrix linked guidelines
  linkedGuidelineIds?: string[];
  // File hash for duplicate detection
  fileHash?: string;
  // Effective date (for Credit Guidelines, Credit Matrix, Seller Guide)
  effectiveDate?: string;

  // Manage Investors v2: UI-level visibility + tab linking (NOT RLS-enforced).
  // hiddenRoles empty/undefined = visible to all; admin/partner are always visible.
  hiddenRoles?: string[];
  // Legacy allow-list storage kept only for migration/fallback compatibility.
  allowedRoles?: string[];
  linkedTabs?: ('processing' | 'closing')[];
}

// Item 11: the document-type catalog is now persisted (investor_document_types), so custom
// admin-created types must validate. DocumentCategory is therefore plain text; DOCUMENT_CATEGORIES
// below remains the built-in seed list (and the source for special-case logic like effective
// dates / sub-types), but is no longer an exhaustive union.
export type DocumentCategory = string;

/** Categories that support an effective date (when the investor's rules take effect) */
export const EFFECTIVE_DATE_CATEGORIES: DocumentCategory[] = ['Credit Guidelines', 'Credit Matrix', 'Seller Guide'];

export const DOCUMENT_CATEGORIES: DocumentCategory[] = [
  'Broker Agreement',
  'Compensation Agreement',
  'Overlay Matrix',
  'Credit Matrix',
  'Signed MLPA',
  'Rate Sheet',
  'Credit Guidelines',
  'Seller Guide',
  'Investor Profile',
  'Licensing Documents',
  'Insurance Certificates',
  'Financial Statements',
  'Compliance Certifications',
  'Servicing Agreement',
  'Quality Control Manual',
  'Delivery Schedule',
  'Wire Instructions',
  'Contact Directory',
  // Live DB values previously missing from this list
  'Correspondent Agreement',
  'Warehouse Agreement',
  // Manage Investors v2 document types
  'Appraisal Policy',
  'Asset Depletion Worksheet',
  'Bank Statement Worksheet',
  'Business Narrative',
  'Contact Sheet',
  'Exception Form',
  'Exclusionary List',
  'FHA/VA Policy',
  'Loan Delivery Checklist',
  'Quick Reference Guide',
  'Secondary/Lock Policy',
  'Submission Form',
  'Submission Instructions',
  'Trailing Docs'
];

// Categories that support sub-types (user must enter a descriptive name)
export const CATEGORIES_WITH_SUBTYPES: DocumentCategory[] = ['Credit Guidelines', 'Credit Matrix'];

// Product Types (updated with new options)
export const PRODUCT_TYPES = [
  'Conforming',
  'FHA',
  'VA',
  'Non-QM',
  'Jumbo',
  'USDA',
  'DSCR',
  'Reverse',
  '2nd Mortgage',
  'HELOC',
  'ITIN',
  'Foreign National',
  'Construction',
  'Commercial',
  '5-8 Units'
] as const;

export type ProductType = typeof PRODUCT_TYPES[number];
